"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import Typography from "@mui/material/Typography"
import Image from "next/image"
import { useState } from "react"
import { useInView } from "react-intersection-observer"
import {
  BasicBanner,
  transitionDelayTime,
  TransitionUp,
  useInViewConfig,
} from "ui"
import Container from "ui/Container"
import { sharedColors } from "ui/theme/colors"
import { extractProperty, getNonUmbracoUrl, Link } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

const StyledLayout = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 64px;
    padding: 88px 0;
  }
`

const FeaturesBox = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 64px 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: flex;
    padding: 0;
    gap: 40px;
  }
  ${({ theme }) => theme.breakpoints.up("xl")} {
    gap: 64px;
  }
`

const StyledFeature = styled(Button, {
  shouldForwardProp: (prop) => prop !== "$isHovered",
})<{ $isHovered: boolean; target?: string }>`
  display: flex;
  height: 162px;
  padding: 24px 16px;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  text-align: center;
  border-radius: 12px;
  border: 1px solid ${sharedColors.borderGray};
  background: ${sharedColors.white};
  transition: 0.3s ease;
  cursor: pointer;
  ${({ theme }) => theme.breakpoints.up("xl")} {
    height: 164px;
  }
  &:hover {
    border: 1px solid ${sharedColors.labelGray};
    box-shadow: 0px 2px 8px 0px rgba(38, 48, 61, 0.16);
  }
  &:focus-visible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const StyledTypography = styled(Typography)`
  min-height: 2.5em;
  ${({ theme }) => theme.breakpoints.up("md")} {
    min-height: auto;
  }
`

const busImages = {
  green: {
    bus: {
      big: "/images/services/busBigGreen.png",
      medium: "/images/services/busMediumGreen.png",
      small: "/images/services/busSmallGreen.png",
    },
    union: {
      big: "/images/services/unionBigGreen.png",
      medium: "/images/services/unionMediumGreen.png",
      small: "/images/services/unionSmallGreen.png",
    },
  },
  white: {
    bus: {
      big: "/images/services/busBigWhite.png",
      medium: "/images/services/busMediumWhite.png",
      small: "/images/services/busSmallWhite.png",
    },
    union: {
      big: "/images/services/unionBigWhite.png",
      medium: "/images/services/unionMediumWhite.png",
      small: "/images/services/unionSmallWhite.png",
    },
  },
}
type Feature = {
  title: string
  icon: string
  url: string
  target?: string
}

function Feature({ title, icon, url, target }: Feature) {
  const [isHovered, setIsHovered] = useState(false)
  const normalizedTitle = title.replace(/\u2028/g, " ")
  return (
    <StyledFeature
      disableRipple
      $isHovered={isHovered}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      LinkComponent={Link}
      href={getNonUmbracoUrl(url)}
      target={target}
    >
      <Image
        src={getUmbracoMediaUrl(icon)}
        alt={title}
        width={56}
        height={56}
      />
      <StyledTypography variant="body1">{normalizedTitle}</StyledTypography>
    </StyledFeature>
  )
}

export default function Services({
  data,
  locale,
}: {
  data: any
  locale: string
}) {
  const itemsListBlock = extractProperty(data, "itemsList") || []
  const items = itemsListBlock.map((i: any) => ({
    icon: getUmbracoMediaUrl(extractProperty(i, "icon")),
    title: extractProperty(i, "link")?.[0]?.name,
    url: extractProperty(i, "link")?.[0]?.url,
    target: extractProperty(i, "link")?.[0]?.target,
  })) as Feature[]
  const { ref, inView } = useInView(useInViewConfig)
  return (
    <Container>
      <StyledLayout ref={ref}>
        <FeaturesBox>
          {items.map((el, i) => (
            <TransitionUp
              key={i}
              $inView={inView}
              $delay={transitionDelayTime * 0.5 * i}
            >
              <Feature
                key={i}
                title={el.title}
                icon={el.icon}
                url={el.url}
                target={el.target}
              />
            </TransitionUp>
          ))}
        </FeaturesBox>
        <TransitionUp $inView={inView} $delay={transitionDelayTime}>
          <BasicBanner data={data} locale={locale} />
        </TransitionUp>
      </StyledLayout>
    </Container>
  )
}
